import { useSyncExternalStore } from "react";
import {
  readSessionStorage,
  subscribeSessionStorage,
  writeSessionStorage
} from "shared/hooks/use-session-storage";

export const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;

export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

const DEFAULT_PLAYBACK_RATE: PlaybackRate = 1;

const STORAGE_KEY = "call-recording:playback-rate";

// The key is fixed, so the subscribe function is created once — passing a new
// one on every render would make useSyncExternalStore resubscribe each time.
const subscribe = subscribeSessionStorage(STORAGE_KEY);

const isPlaybackRate = (value: number): value is PlaybackRate =>
  (PLAYBACK_RATES as readonly number[]).includes(value);

const getSnapshot = (): PlaybackRate => {
  // Number(null) and Number("") are 0, which fails the check — anything not on
  // the list (stale key, hand-edited storage) falls back to normal speed.
  const stored = Number(readSessionStorage(STORAGE_KEY));
  return isPlaybackRate(stored) ? stored : DEFAULT_PLAYBACK_RATE;
};

const getServerSnapshot = (): PlaybackRate => DEFAULT_PLAYBACK_RATE;

/**
 * Playback speed shared by every call recording in the tab (CRM-1330). Picking
 * 1.5x on one call carries to the next recording opened in the same session;
 * a new tab starts back at 1x.
 */
export const usePlaybackRate = (): PlaybackRate =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

export const setPlaybackRate = (rate: PlaybackRate) => {
  writeSessionStorage(STORAGE_KEY, String(rate));
};

export const formatPlaybackRate = (rate: PlaybackRate) => `${rate}x`;
